import React, { useState, useEffect } from "react";
import {
  Box,
  Select,
  TextField,
  IconButton,
  Typography,
  Button,
  Snackbar,
  Tab,
  Tabs,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import ShareIcon from '@mui/icons-material/Share';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAuth } from "../AuthContext";
import useAuthenticatedFetch from '../hooks/useAuthenticatedFetch';
import { useTranslation } from "react-i18next";
import { useMediaQuery } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { ConfirmDialog } from '../components/dialogs/ConfirmDialog';
import SectionExportImage from '../utils/SectionExportImage';
import { updateVariableTimes } from '../utils/scheduleUtils';

const ScheduleEditor = ({ onIsDirtyChange }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [rooms, setRooms] = useState([]);
  const [sections, setSections] = useState([]);
  const [tefilot, setTefilot] = useState({});
  const [warning, setWarning] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const fetchWithAuth = useAuthenticatedFetch();
  const { i18n } = useTranslation();
  const language = i18n.language;
  const [activeTab, setActiveTab] = useState("Week");
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [isDirty, setIsDirty] = useState(false);
  const [newSectionDialog, setNewSectionDialog] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, sectionId: null });
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editedSectionName, setEditedSectionName] = useState('');
  const [fontSize, setFontSize] = useState("16");
  const [community, setCommunity] = useState(null);
  const [hasFixedShabbatTime, setHasFixedShabbatTime] = useState(false);
  const [fixedShabbatTime, setFixedShabbatTime] = useState("18:00");
  const [exportingSection, setExportingSection] = useState(null);
  const [exportImageBlob, setExportImageBlob] = useState(null);
  const [zmanim, setZmanim] = useState(null);
  const [parasha, setParasha] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (onIsDirtyChange) {
      onIsDirtyChange(isDirty);
    }
  }, [isDirty, onIsDirtyChange]);

  // Chargement des données
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Charger les données de la communauté
        const communityResponse = await fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/community/getCommunityById/${user.communityId}`
        );
        if (!communityResponse.ok) throw new Error(t("schedule.errors.loadCommunity"));
        const communityData = await communityResponse.json();
        setCommunity(communityData);

        // Initialiser l'état de l'heure fixe de Shabbat
        if (communityData.candleLightHour) {
          setHasFixedShabbatTime(true);
          setFixedShabbatTime(communityData.candleLightHour);
        }

        // Charger les salles
        const roomsResponse = await fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/tefilot/getRooms?communityId=${user.communityId}`
        );
        if (!roomsResponse.ok) throw new Error(t("schedule.errors.loadRooms"));
        const roomsData = await roomsResponse.json();
        setRooms(roomsData);

        // Charger les sections et tefilot
        await loadSectionsAndTefilot();

      } catch (error) {
        console.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user.communityId, language, activeTab]);

  // Charger la taille de police initiale
  useEffect(() => {
    if (community?.fontSizeSetting) {
      const settings = JSON.parse(community.fontSizeSetting);
      const currentSetting = settings.find(setting => setting.name === activeTab);
      if (currentSetting) {
        setFontSize(currentSetting.size);
      }
    }
  }, [activeTab, community]);

  // Recharger les zmanim quand la communauté change
  useEffect(() => {
    if (community) {
      loadZmanimAndParasha();
    }
  }, [community]);

  const loadSectionsAndTefilot = async () => {
    try {
      // Charger les sections pour l'onglet actif
      const sectionsResponse = await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/tefilot/getPrayerSection?communityId=${user.communityId}&category=${activeTab}`
      );
      if (!sectionsResponse.ok) throw new Error(t("schedule.errors.loadSections"));
      const sectionsData = await sectionsResponse.json();
      setSections(sectionsData);

      // Charger les tefilot
      const tefilotResponse = await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/tefilot/getTefilotCommunities?communityId=${user.communityId}`
      );
      if (!tefilotResponse.ok) throw new Error(t("schedule.errors.loadTefilot"));
      const tefilotData = await tefilotResponse.json();
      
      // Organiser les tefilot par section
      const tefilotBySection = tefilotData.reduce((acc, tefilah) => {
        if (!acc[tefilah.sectionId]) {
          acc[tefilah.sectionId] = [];
        }
        acc[tefilah.sectionId].push(tefilah);
        return acc;
      }, {});
      
      setTefilot(tefilotBySection);

      // Charger les zmanim et parasha pour le traitement des horaires dynamiques
      await loadZmanimAndParasha();
    } catch (error) {
      console.error(error);
      setErrorMessage(t("schedule.errors.loadData"));
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    loadSectionsAndTefilot();
  };

  const loadZmanimAndParasha = async () => {
    try {
      if (!community?.latitude || !community?.longitude || !community?.timezone) {
        console.warn("Coordonnées de la communauté non disponibles pour les zmanim");
        return;
      }

      const now = new Date();
      const currentDate = now.toLocaleDateString('en-CA');

      // Charger les zmanim
      const zmanimResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/zmanim/getZmanim?latitude=${community.latitude}&longitude=${community.longitude}&timezone=${community.timezone}&date=${currentDate}&communityName=${community.nameForUrl}`
      );
      if (zmanimResponse.ok) {
        const zmanimData = await zmanimResponse.json();
        setZmanim(zmanimData);
      }

      // Charger la parasha
      const parashaResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/zmanim/getParasha?language=${community.displayLanguage}&latitude=${community.latitude}&longitude=${community.longitude}&timezone=${community.timezone}&communityName=${community.nameForUrl}`
      );
      if (parashaResponse.ok) {
        const parashaData = await parashaResponse.json();
        setParasha(parashaData);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des zmanim/parasha:", error);
    }
  };



  const handleAddSection = async () => {
    if (!newSectionName.trim()) return;

    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/tefilot/savePrayerSection`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            communityId: user.communityId,
            sectionName: newSectionName,
            category: activeTab,
            rank: sections.length
          })
        }
      );

      if (!response.ok) throw new Error(t("schedule.errors.saveSection"));
      
      const newSection = await response.json();
      setSections([...sections, newSection]);
      setTefilot({ ...tefilot, [newSection.sectionId]: [] });
      setNewSectionName('');
      setNewSectionDialog(false);
      setSuccessMessage(t("schedule.success.addSection"));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error(error);
      const code = error?.code || error?.details?.code;
      if (code === "PLAN_LIMIT") {
        setErrorMessage(t("schedule.errors.planLimitPro"));
      } else if (code === "FEATURE_PRO_ONLY") {
        setErrorMessage(t("schedule.errors.proOnly"));
      } else {
        setErrorMessage(t("schedule.errors.saveSection"));
      }
    }
  };

  const handleDeleteSection = async (sectionId) => {
    setDeleteConfirmDialog({ open: true, sectionId });
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/tefilot/deletePrayerSection/${deleteConfirmDialog.sectionId}?communityId=${user.communityId}`,
        { method: 'DELETE' }
      );

      if (!response.ok) throw new Error(t("schedule.errors.deleteSection"));

      setSections(sections.filter(section => section.sectionId !== deleteConfirmDialog.sectionId));
      const newTefilot = { ...tefilot };
      delete newTefilot[deleteConfirmDialog.sectionId];
      setTefilot(newTefilot);
      setSuccessMessage(t("schedule.success.deleteSection"));
    } catch (error) {
      console.error(error);
      setErrorMessage(t("schedule.errors.deleteSection"));
    } finally {
      setDeleteConfirmDialog({ open: false, sectionId: null });
    }
  };

  const handleAddTefilah = (sectionId) => {
    const newTefilot = {
      ...tefilot,
      [sectionId]: [
        ...(tefilot[sectionId] || []),
        {
          sectionId,
          name: "",
          roomId: "",
          time: "",
          isVariable: false,
          zman: "",
          offSetValue: 0,
          around5m: false,
          startDate: "",
          endDate: "",
        }
      ]
    };
    setTefilot(newTefilot);
    setIsDirty(true);
  };

  const handleUpdateTefilah = (sectionId, index, field, value) => {
    const updatedTefilot = {
      ...tefilot,
      [sectionId]: tefilot[sectionId].map((t, i) => 
        i === index ? { ...t, [field]: value } : t
      )
    };
    setTefilot(updatedTefilot);
    setIsDirty(true);
  };

  const handleDeleteTefilah = (sectionId, index) => {
    const updatedTefilot = {
      ...tefilot,
      [sectionId]: tefilot[sectionId].filter((_, i) => i !== index)
    };
    setTefilot(updatedTefilot);
    setIsDirty(true);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const newSections = Array.from(sections);
    const [reorderedSection] = newSections.splice(result.source.index, 1);
    newSections.splice(result.destination.index, 0, reorderedSection);

    // Mise à jour des rangs
    const updatedSections = newSections.map((section, index) => ({
      ...section,
      rank: index
    }));

    setSections(updatedSections);
    setIsDirty(true);

    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/tefilot/updateSectionsOrder`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sections: updatedSections })
        }
      );

      if (!response.ok) throw new Error(t("schedule.errors.updateOrder"));
    } catch (error) {
      console.error(error);
      setErrorMessage(t("schedule.errors.updateOrder"));
    }
  };

  const handleSave = async () => {
    try {
      // Sauvegarder la taille de police en utilisant les données existantes
      const currentSettings = community.fontSizeSetting ? JSON.parse(community.fontSizeSetting) : [];
      const updatedSettings = currentSettings.map(setting => 
        setting.name === activeTab 
          ? { ...setting, size: fontSize }
          : setting
      );

      if (!updatedSettings.some(setting => setting.name === activeTab)) {
        updatedSettings.push({ name: activeTab, size: fontSize });
      }

      await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/community/updateFontSizeSettings`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            communityId: user.communityId,
            fontSizeSetting: JSON.stringify(updatedSettings),
          }),
        }
      );

      // Sauvegarder l'heure fixe de Shabbat si elle est activée
      if (hasFixedShabbatTime) {
        await fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/community/updateCandleLightHour`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              communityId: user.communityId,
              candleLightHour: fixedShabbatTime,
            }),
          }
        );
      } else {
        // Si la case est décochée, envoyer null
        await fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/community/updateCandleLightHour`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              communityId: user.communityId,
              candleLightHour: null,
            }),
          }
        );
      }

      // Ensuite sauvegarder les tefilot
      const flattenedTefilot = Object.values(tefilot)
        .flat()
        .map(t => ({
          ...t,
          communityId: user.communityId,
          time: t.isVariable ? "00:00" : (t.time || "00:00")
        }));

      const tefilotResponse = await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/tefilot/saveTefilotCommunities`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            communityId: user.communityId,
            rows: flattenedTefilot
          })
        }
      );

      if (!tefilotResponse.ok) throw new Error(t("schedule.errors.save"));

      const savePayload = await tefilotResponse.json().catch(() => ({}));
      if (savePayload.partialSave) {
        await loadSectionsAndTefilot();
        setErrorMessage(t("schedule.success.partialSave", { max: savePayload.truncated?.[0]?.max ?? 2 }));
        setTimeout(() => setErrorMessage(''), 8000);
      } else {
        setSuccessMessage(t("schedule.success.save"));
        setTimeout(() => setSuccessMessage(''), 3000);
      }
      setIsDirty(false);
    } catch (error) {
      console.error(error);
      const code = error?.code || error?.details?.code;
      if (code === "PLAN_LIMIT") {
        setErrorMessage(t("schedule.errors.planLimitPro"));
      } else if (code === "FEATURE_PRO_ONLY") {
        setErrorMessage(t("schedule.errors.proOnly"));
      } else {
        setErrorMessage(t("schedule.errors.save"));
      }
    }
  };

  const handleUpdateSectionName = async (sectionId, newName) => {
    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/tefilot/updatePrayerSection`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sectionId,
            sectionName: newName,
          })
        }
      );

      if (!response.ok) throw new Error(t("schedule.errors.updateSection"));

      setSections(sections.map(section => 
        section.sectionId === sectionId 
          ? { ...section, sectionName: newName }
          : section
      ));
      setSuccessMessage(t("schedule.success.updateSection"));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error(error);
      setErrorMessage(t("schedule.errors.updateSection"));
    }
  };

  const handleStartEditing = (section) => {
    setEditingSectionId(section.sectionId);
    setEditedSectionName(section.sectionName);
  };

  const handleSaveSection = async (sectionId) => {
    await handleUpdateSectionName(sectionId, editedSectionName);
    setEditingSectionId(null);
  };

  const handleExportSection = (section) => {
    if (isExporting) return; // Éviter les appels multiples
    
    setIsExporting(true);
    
    // Récupérer les tefilot de la section
    const sectionTefilot = tefilot[section.sectionId] || [];
    
    // Traiter les horaires dynamiques
    const processedTefilot = updateVariableTimes(sectionTefilot, zmanim, parasha, language, community);
    
    // Formater les données pour l'export
    const exportData = processedTefilot.map(tefilah => ({
      name: tefilah.name,
      roomName: rooms.find(room => room.roomId === tefilah.roomId)?.roomName || '',
      time: tefilah.time || ''
    }));
    
    setExportingSection({
      ...section,
      processedData: exportData
    });
  };

  const handleImageGenerated = (blob) => {
    setExportImageBlob(blob);
    const section = exportingSection;
    setExportingSection(null);
    setIsExporting(false);
    
    // Créer un lien de téléchargement
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${section.sectionName}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    setSuccessMessage(t("schedule.success.exportImage"));
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Compter le nombre de tefilot par catégorie
  const getTefilotCount = (category) => {
    return Object.values(tefilot)
      .flat()
      .filter(t => t.category === category)
      .length;
  };

  if (loading) {
    return <Typography>{t("common.loading")}</Typography>;
  }

  return (
    <Box sx={{ mt: 1, width: '100%' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          {t("schedule.title")}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!isDirty}
          >
            {t('schedule.save')}
          </Button>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        centered={!isSmallScreen}
        variant={isSmallScreen ? "scrollable" : "standard"}
        scrollButtons={isSmallScreen ? "auto" : false}
        allowScrollButtonsMobile
      >
        <Tab label={`${t("schedule.category.Week")} (${getTefilotCount("Week")})`} value="Week" />
        <Tab label={`${t("schedule.category.Shabat")} (${getTefilotCount("Shabbat")})`} value="Shabbat" />
      </Tabs>

      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setNewSectionDialog(true)}
          >
            {t("schedule.addSection")}
          </Button>
          
          <TextField
            label={t("schedule.fontSize")}
            type="number"
            value={fontSize}
            onChange={(e) => {
              setFontSize(e.target.value);
              setIsDirty(true);
            }}
            size="small"
            sx={{ width: '120px' }}
            InputProps={{
              endAdornment: <span style={{ marginLeft: '4px' }}>px</span>
            }}
          />
        </Box>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="sections">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{ minHeight: '100px' }}
              >
                {sections.map((section, index) => (
                  <Draggable
                    key={section.sectionId}
                    draggableId={String(section.sectionId)}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          ...provided.draggableProps.style,
                          marginBottom: '8px'
                        }}
                      >
                        <Accordion>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              width: '100%',
                              gap: 2 
                            }}>
                              <div {...provided.dragHandleProps}>
                                <DragIndicatorIcon />
                              </div>
                              {editingSectionId === section.sectionId ? (
                                <>
                                  <TextField
                                    value={editedSectionName}
                                    onChange={(e) => setEditedSectionName(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={(e) => e.stopPropagation()}
                                    variant="standard"
                                    sx={{ minWidth: 150 }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSaveSection(section.sectionId);
                                    }}
                                    color="primary"
                                  >
                                    <CheckIcon />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteSection(section.sectionId);
                                    }}
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingSectionId(null);
                                    }}
                                    color="default"
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </>
                              ) : (
                                <>
                                  <Typography>{section.sectionName}</Typography>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStartEditing(section);
                                    }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </>
                              )}
                              <Box sx={{ flexGrow: 1 }} />
                              <Tooltip title={t("schedule.exportSection")}>
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleExportSection(section);
                                  }}
                                  size="small"
                                  sx={{ mr: 1 }}
                                  disabled={isExporting}
                                >
                                  <ShareIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            {(tefilot[section.sectionId] || []).map((tefilah, tefilahIndex) => (
                              <Box 
                                key={tefilahIndex}
                                sx={{ 
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 2,
                                  mb: 2,
                                  pb: 2,
                                  borderBottom: '1px solid #eee'
                                }}
                              >
                                <TextField
                                  label={t("schedule.tefilahName")}
                                  value={tefilah.name || ""}
                                  onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "name", e.target.value)}
                                  sx={{ flex: '1 1 200px' }}
                                />

                                <FormControl sx={{ flex: '1 1 200px' }}>
                                  <Select
                                    value={tefilah.roomId || ""}
                                    onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "roomId", e.target.value)}
                                    displayEmpty
                                  >
                                    <MenuItem value="">{t('schedule.selectRoom')}</MenuItem>
                                    {rooms.map((room) => (
                                      <MenuItem key={room.roomId} value={room.roomId}>
                                        {room.roomName}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>

                                <FormControl sx={{ flex: '1 1 200px' }}>
                                  <Select
                                    value={Boolean(tefilah.isVariable)}
                                    onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "isVariable", e.target.value)}
                                    displayEmpty
                                  >
                                    <MenuItem value="">{t('schedule.timeType')}</MenuItem>
                                    <MenuItem value={false}>{t('schedule.fixedTime')}</MenuItem>
                                    <MenuItem value={true}>{t('schedule.variableTime')}</MenuItem>
                                  </Select>
                                </FormControl>

                                {!Boolean(tefilah.isVariable) ? (
                                  <TextField
                                    type="time"
                                    value={tefilah.time || ""}
                                    onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "time", e.target.value)}
                                    sx={{ flex: '1 1 200px' }}
                                  />
                                ) : (
                                  <>
                                    <FormControl sx={{ flex: '1 1 200px' }}>
                                      <Select
                                        value={tefilah.zman || ""}
                                        onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "zman", e.target.value)}
                                      >
                                        <MenuItem value="">{t('schedule.selectZman')}</MenuItem>
                                        <MenuItem value="Netz">{t('schedule.zman.netz')}</MenuItem>
                                        <MenuItem value="Chatsoth">{t('schedule.zman.chatsoth')}</MenuItem>
                                        <MenuItem value="MinhaGuedola">{t('schedule.zman.minhaGuedola')}</MenuItem>
                                        <MenuItem value="MinhaKetana">{t('schedule.zman.minhaKetana')}</MenuItem>
                                        <MenuItem value="PlagHaMincha">{t('schedule.zman.plagHaMincha')}</MenuItem>
                                        <MenuItem value="CandleLighting">{t('schedule.zman.candleLighting')}</MenuItem>
                                        <MenuItem value="Shekia">{t('schedule.zman.shekia')}</MenuItem>
                                        <MenuItem value="Stars">{t('schedule.zman.stars')}</MenuItem>
                                        <MenuItem value="Havdalah">{t('schedule.zman.havdalah')}</MenuItem>
                                      </Select>
                                    </FormControl>

                                    <TextField
                                      type="number"
                                      label={t('schedule.offset')}
                                      value={tefilah.offSetValue || 0}
                                      onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "offSetValue", e.target.value)}
                                      sx={{ flex: '1 1 200px' }}
                                    />
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={tefilah.around5m || false}
                                          onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "around5m", e.target.checked)}
                                        />
                                      }
                                      label={t('schedule.around5m')}
                                      sx={{ flex: '1 1 200px' }}
                                    />
                                  </>
                                )}

                                <TextField
                                  type="date"
                                  label={t('schedule.startDate')}
                                  value={tefilah.startDate || ""}
                                  onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "startDate", e.target.value)}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ flex: '1 1 200px' }}
                                />

                                <TextField
                                  type="date"
                                  label={t('schedule.endDate')}
                                  value={tefilah.endDate || ""}
                                  onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "endDate", e.target.value)}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ flex: '1 1 200px' }}
                                />

                                <IconButton
                                  onClick={() => handleDeleteTefilah(section.sectionId, tefilahIndex)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            ))}
                            <Button
                              startIcon={<AddIcon />}
                              onClick={() => handleAddTefilah(section.sectionId)}
                            >
                              {t("schedule.addTefilah")}
                            </Button>
                          </AccordionDetails>
                        </Accordion>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Box>

      <Dialog open={newSectionDialog} onClose={() => setNewSectionDialog(false)}>
        <DialogTitle>{t("schedule.newSection")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("schedule.sectionName")}
            fullWidth
            value={newSectionName}
            onChange={(e) => setNewSectionName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewSectionDialog(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleAddSection} color="primary">
            {t("common.add")}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        {activeTab === "Shabbat" && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography>{t("schedule.fixedShabbatTime")}</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasFixedShabbatTime}
                  onChange={(e) => {
                    setHasFixedShabbatTime(e.target.checked);
                    setIsDirty(true);
                  }}
                />
              }
              label=""
            />
            {hasFixedShabbatTime && (
              <TextField
                type="time"
                value={fixedShabbatTime}
                onChange={(e) => {
                  setFixedShabbatTime(e.target.value);
                  setIsDirty(true);
                }}
                sx={{ width: '150px' }}
              />
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!isDirty}
          >
            {t('schedule.save')}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />
      <Snackbar
        open={!!warning}
        autoHideDuration={3000}
        onClose={() => setWarning('')}
        message={warning}
      />
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={3000}
        onClose={() => setErrorMessage('')}
        message={errorMessage}
      />

      <ConfirmDialog
        open={deleteConfirmDialog.open}
        title={t("schedule.confirmDelete")}
        message={t("schedule.confirmDeleteMessage")}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirmDialog({ open: false, sectionId: null })}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        severity="error"
      />

      {/* Composant d'export d'image - masqué en permanence */}
      <Box sx={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden' }}>
        {exportingSection && (
          <SectionExportImage
            sectionName={exportingSection.sectionName}
            scheduleItems={exportingSection.processedData}
            onImageGenerated={handleImageGenerated}
          />
        )}
      </Box>
    </Box>
  );
};

export default ScheduleEditor;
